// U4 · Your collection (QU4_Collection): keepsakes in route order (locked ones say "Level N" / "Coming soon"; tap an
// earned one for the buddy's line from keepsakes.ts) and the stamped done cards (state.collection, newest first).
import { useRef, useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { keepLine } from '@shared/content/keepsakes';
import { stampDate } from '@shared/rules';
import type { RouteStop } from '@shared/types';
import { useApp } from '../../store';
import { back } from '../../router';
import { Screen, ScrollArea, IconButton, XKeep, XCard, BottomSheet, PrimaryButton } from '../../components';
import { useSize } from '../../lib/useSize';
import { keepStops, lockedLabel, plural } from './route';
import { US } from './copy';
import s from './us.module.css';

const TILT = [-2, 1.5, -1];

export function CollectionScreen() {
  const st = useApp((x) => x.state);
  const grid = useRef<HTMLDivElement>(null);
  const { w } = useSize(grid, { w: 326, h: 200 });
  const [shown, setShown] = useState<RouteStop | null>(null);
  if (!st) return <Screen theme="margo" id="U4" />;
  const ch = st.ch, c = C[ch], b = st.buddy;
  const keeps = keepStops(b);
  const ks = Math.max(48, Math.min(80, Math.floor((w - 30) / 4)));
  const cards = st.collection.recent;
  const total = st.collection.cardsDone;

  return (
    <Screen theme={ch} id="U4">
      <header class={s.bar}>
        <IconButton icon="back" label="Back" class={s.back} onClick={() => back('U1')} />
        <div>
          <h1 class={s.barTitle}>Your collection</h1>
          <p class={s.barSub}>{`${plural(b.keepsakes.length, 'keepsake')} · ${plural(total, 'card')}`}</p>
        </div>
      </header>
      <ScrollArea class={s.colScroll}>
        <div class={`${s.list} ${s.colList}`}>
          <p class={s.label}>KEEPSAKES</p>
          <div class={s.keepGrid} ref={grid} style={{ marginTop: 0 }}>
            {keeps.map((r) => {
              const art = <XKeep ch={ch} i={r.keep ?? 0} s={ks} locked={!r.reached} alt="" />;
              const name = <span class={s.keepName} data-locked={r.reached ? 'false' : 'true'}>{r.reached ? r.label : lockedLabel(r)}</span>;
              return r.reached
                ? <button type="button" class={s.keepBtn} key={r.idx} onClick={() => setShown(r)}>{art}{name}</button>
                : <div class={s.keepBtn} key={r.idx}>{art}{name}</div>;
            })}
          </div>
          <p class={s.muted}>{US.keepNote}</p>

          <p class={s.label} style={{ marginTop: 14 }}>CARDS DONE</p>
          <div class={s.cardGrid}>
            {cards.map((x, i) => (
              <div class={s.cardCell} key={x.id}>
                <XCard ch={ch} face="front" w={104} h={76} title={x.title} stamped stamp="DONE" foot="" tilt={TILT[i % 3]} />
                <span class={s.cardDate}>{stampDate(x.day)}</span>
              </div>
            ))}
          </div>
          <p class={s.more}>{total > cards.length ? `${plural(total - cards.length, 'more card', 'more cards')} before these` : US.allCards}</p>
        </div>
      </ScrollArea>

      <BottomSheet open={!!shown} onClose={() => setShown(null)} label={shown ? shown.label : 'Keepsake'}>
        {shown && (
          <div class={s.sheetKeep}>
            <XKeep ch={ch} i={shown.keep ?? 0} s={132} />
            <p class={s.sheetLv}>{`LEVEL ${shown.level}`}</p>
            <p class={s.sheetName}>{shown.label}</p>
            <p class={s.sheetLine}>{`“${keepLine(ch, shown.keep ?? 0)}”`}</p>
            <p class={s.sheetBy}>{`— ${c.name}`}</p>
            <PrimaryButton h={52} style={{ alignSelf: 'stretch', marginTop: 6 }} onClick={() => setShown(null)}>Close</PrimaryButton>
          </div>
        )}
      </BottomSheet>
    </Screen>
  );
}
